'use client';

import { faCalendarDays, faFlask } from '@fortawesome/pro-light-svg-icons';
import { Laboratory, LaboratoryExam } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { tv } from 'tailwind-variants';

import { Button } from '@/components/ui/button/button.component';
import { Card } from '@/components/ui/card.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const branchLaboratoryAsideCardStyle = tv({
  slots: {
    card: 'w-full xl:max-w-lg 2xl:max-w-lg flex-1 mt-4 xl:mt-0 rounded-xl px-10 py-8 sticky top-6',
  },
});

export interface BranchLaboratoryAsideCard extends React.HTMLAttributes<HTMLDivElement> {
  laboratory: Laboratory;
  exams: LaboratoryExam[] | null;
}

export function BranchLaboratoryAsideCard({ className, laboratory, exams }: BranchLaboratoryAsideCard) {
  const { card } = branchLaboratoryAsideCardStyle();
  const { buildLink } = useAgendamentoNavigation();

  const branchExams = exams?.slice(0, 5);

  return (
    <Card className={card({ className })}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start gap-2">
          <Icon icon={faFlask} className="text-2xl mt-1 md:mt-0.5 text-blue-600 w-6 h-6" />

          <div className="max-w-lg">
            <Typography variant={TypographyVariant.BODY_STRONG} className="break-words text-wrap hyphens-auto overflow-hidden">
              Agende exames no
              {' '}
              {resolveLaboratoryName(laboratory)}
            </Typography>
          </div>
        </div>

        <Button
          data-testid={DataTestIdEnum.BRANCH_LABORATORY_PROFILE_EXAMS_BUTTON}
          startIcon={faCalendarDays}
          data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
          asChild
        >
          <Link
            prefetch={false}
            href={buildLink({ path: `/laboratorio/${laboratory.id}/` })}
          >
            Exames e horários
          </Link>
        </Button>

        {Number(branchExams?.length) > 0 && (
          <div>
            <span className="text-gray-700">Veja alguns exames oferecidos:</span>

            <ul className="mt-2 list-disc space-y-2">
              {branchExams?.map((exam) => (
                <li
                  data-testid={`branch-laboratory-profile--exam=${exam.procedureId}`}
                  key={exam.procedureId}
                  className="text-gray-700 ml-6"
                >
                  {exam.procedureName}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
